/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources } from '@msdyn365-commerce/components';
import * as Msdyn365 from '@msdyn365-commerce/core';

import { ICoreContext } from '@msdyn365-commerce/core';
import { ProductPrice, ProductSearchCriteria, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    DimensionAvailabilitiesForProductSearchResultInput,
    DimensionTypes,
    getDimensionAvailabilitiesForProductSearchResultAction,
    getPriceForProductSearchResult,
    IDimensionsApp,
    ObjectExtensions,
    PriceForProductSearchResultInput,
    generateImageUrl
} from '@msdyn365-commerce-modules/retail-actions';
import {
    getTelemetryObject,
    IComponentNodeProps,
    IModuleProps,
    INodeProps,
    ISingleSlideCarouselProps,
    ITelemetryContent,
    NodeTag,
    SingleSlideCarousel
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { ISgiNewArrivalData } from './sgi-new-arrival.data';
import { ISgiNewArrivalProps, layout } from './sgi-new-arrival.props.autogenerated';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getAttributeByKeyName } from '../../themes/sgi-theme/views/Utilities/utility';
import ProductComponent from '../../themes/sgi-theme/views/components/product.component';

export interface IProductCollectionViewProps extends ISgiNewArrivalProps<ISgiNewArrivalData> {
    heading?: React.ReactNode;
    ProductCollectionContainer: IModuleProps;
    SingleSlideCarouselComponentProps: INodeProps;
    GridComponentProps: INodeProps;
    products: IProductComponentViewProps[];
    isCarousel: boolean;
    productPrices?: ProductPrice[];
}

export interface IProductComponentViewProps {
    ProductContainer: INodeProps;
    productComponent: React.ReactNode;
}
export interface IProductComponentState {
    products: ProductSearchResult[];
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
}

/**
 * ProductCollection component.
 */
class ProductCollection extends React.PureComponent<ISgiNewArrivalProps<ISgiNewArrivalData>, IProductComponentState> {
    private readonly telemetryContent: ITelemetryContent;

    private readonly productPrices: ProductPrice[];

    constructor(props: ISgiNewArrivalProps<ISgiNewArrivalData>) {
        super(props);
        this.productPrices = [];
        this.state = {
            products: []
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );

        this.getProductBasedOnRefinerAsync()
            .then(products => {
                if (products) {
                    const filteredproducts = products.filter(product => {
                        const isNewArrival = getAttributeByKeyName(product.AttributeValues, this.props.config.newArrivalAttrKeyName)
                            ?.BooleanValue;
                        const isNewArrivalDateValid = getAttributeByKeyName(
                            product?.AttributeValues,
                            this.props.config.newArrivalDateAttrKeyName
                        )?.DateTimeOffsetValue;
                        return isNewArrival && isNewArrivalDateValid && isNewArrivalDateValid >= new Date() && product;
                    });
                    if (filteredproducts && filteredproducts.length > 0) {
                        this.setState({ products: filteredproducts });
                    }
                }
            })
            .catch(err => console.log('Error [getProductBasedOnRefinerAsync]', err));
    }

    public componentDidMount(): void {
        if (this.props.config.enableAffiliationBasedPricing && this.props.context.actionContext.requestContext.user.isAuthenticated) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Initialize data loading.
            this._getActivePrice();
        }
    }

    private getProductBasedOnRefinerAsync = async (): Promise<ProductSearchResult[] | null> => {
        const {
            config: { newArrivalRefinerName, newArrivalRefinerLVSValue }
        } = this.props;
        if (newArrivalRefinerName && newArrivalRefinerLVSValue) {
            return this.props.data.refiners
                .then(refiners => {
                    const filteredRefiner = refiners
                        ?.find(refiner => refiner.KeyName?.toLowerCase() === newArrivalRefinerName.toLowerCase())
                        ?.Values.find(value => value.LeftValueBoundString?.toLowerCase() === newArrivalRefinerLVSValue.toLowerCase());
                    if (filteredRefiner) {
                        const productSearchCriteria: ProductSearchCriteria = {
                            Refinement: [filteredRefiner],
                            Context: {
                                ChannelId: +this.props.context.actionContext.requestContext.apiSettings.channelId,
                                CatalogId: +this.props.context.actionContext.requestContext.apiSettings.catalogId
                            },
                            IncludeAttributes: true,
                            SkipVariantExpansion: true
                        };
                        return searchByCriteriaAsync({ callerContext: this.props.context.actionContext }, productSearchCriteria);
                    }

                    return null;
                })
                .catch(() => null);
        }
        return null;
    };
    public render(): JSX.Element | null {
        const { heading, className } = this.props.config;
        const { resources } = this.props;

        // Initiallize state in case if the products were not found during initial module render
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Initialize data if not there.
        // this.initProductState(this.props.config.productCollection);

        const products = this.state.products;
        const isCarousel = this.props.config.layout === layout.carousel;
        const headingLabel = heading ? heading.text : '';
        const headingComponent = heading && (
            <Msdyn365.Text
                className='ms-product-collection__heading'
                tag={heading.tag || 'h1'}
                text={headingLabel}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- product check for safety.
        const updatedProducts = products?.map((item: ProductSearchResult, index: number) => {
            const getProductData = this._getProduct(item, index);
            return !ObjectExtensions.isNullOrUndefined(getProductData) ? getProductData : undefined;
        });

        const productCollectionViewProps = {
            ...this.props,
            heading: headingComponent,
            SingleSlideCarouselComponentProps: {
                tag: SingleSlideCarousel,
                className: '',
                flipperPrevLabel: `${headingLabel} ${resources.flipperPrevious}`,
                flipperNextLabel: `${headingLabel} ${resources.flipperNext}`,
                parentId: this.props.id,
                telemetryContent: this.telemetryContent
            } as IComponentNodeProps<ISingleSlideCarouselProps>,
            ProductCollectionContainer: {
                moduleProps: this.props,
                className: classnames('ms-product-collection', className)
            },
            GridComponentProps: {
                tag: 'ul' as NodeTag,
                className: 'ms-product-collection__items'
            },
            isCarousel,

            products:
                ArrayExtensions.hasElements(updatedProducts) &&
                updatedProducts.filter(item => {
                    return item !== undefined;
                }),
            productPrices: this.productPrices
        };
        return this.props.renderView(productCollectionViewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);

    /**
     * Get products dimension availabilities.
     * @param products - Products.
     * @returns Promise.
     */
    private readonly _getProductsDimensionsAvailabilities = async (products: ProductSearchResult[]): Promise<void> => {
        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = context.app.config.dimensionsInProductCard;
        const dimensionsToDisplayAsSwatch = context.app.config.dimensionsAsSwatchType;
        const validProducts = ArrayExtensions.validValues(products);
        if (
            ArrayExtensions.hasElements(validProducts) &&
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            ArrayExtensions.hasElements(dimensionsToDisplayAsSwatch) &&
            !dimensionsToDisplayAsSwatch.includes(DimensionTypes.none)
        ) {
            const productsDimensionAvailabilities = await getDimensionAvailabilitiesForProductSearchResultAction(
                new DimensionAvailabilitiesForProductSearchResultInput(validProducts, this.props.context as ICoreContext<IDimensionsApp>),
                this.props.context.actionContext
            );

            if (ArrayExtensions.hasElements(productsDimensionAvailabilities)) {
                this.setState({ productsDimensionAvailabilities });
            }
        }
    };

    private readonly _getProduct = (product: ProductSearchResult, index: number): IProductComponentViewProps | null => {
        if (!product || !product.Name) {
            this.props.telemetry.trace('[product-collection] Product or product name is null');
            return null;
        }
        product.PrimaryImageUrl = generateImageUrl(product?.PrimaryImageUrl, this.props.context.request.apiSettings);
        const telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        const { imageSettings, allowBackNavigation } = this.props.config;
        const resources = this.props.resources;
        const breaCrumbType = this.props.context.app.config.breadcrumbType;
        const allowBack = allowBackNavigation && (breaCrumbType === 'back' || breaCrumbType === 'categoryAndBack');
        const quickviewSlot =
            this.props.slots && this.props.slots.quickview && this.props.slots.quickview.length > 0 ? this.props.slots.quickview[0] : null;
        const reviewCount = 1;
        const reviewTotalRatings = Number(product.TotalRatings);
        const reviewCountAriaLabelValue =
            reviewTotalRatings > reviewCount ? resources.ratingCountAriaLabel : resources.ratingCountAriaLabelSingleUser;
        const isPriceMinMaxEnabled = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );
        const priceResources: IPriceComponentResources = {
            priceRangeSeparator: resources.priceRangeSeparator
        };
        return {
            ProductContainer: {
                tag: 'li' as NodeTag,
                className: 'ms-product-collection__item',
                role: 'listitem',
                key: index
            },
            productComponent: (
                <ProductComponent
                    context={this.props.context}
                    telemetryContent={telemetryContent}
                    imageSettings={imageSettings}
                    freePriceText={resources.priceFree}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    ratingAriaLabel={resources.ratingAriaLabel}
                    ratingCountAriaLabel={reviewCountAriaLabelValue}
                    allowBack={allowBack}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{ product }}
                    quickViewButton={quickviewSlot}
                    isPriceMinMaxEnabled={isPriceMinMaxEnabled?.IsEnabled}
                    priceResources={priceResources}
                    dimensionAvailabilities={this.state.productsDimensionAvailabilities?.find(
                        dimensionAvailability =>
                            ArrayExtensions.hasElements(dimensionAvailability) &&
                            // eslint-disable-next-line
                            dimensionAvailability[0].masterProductId === (product.MasterProductId ?? product.RecordId)
                    )}
                />
            )
        };
    };

    /**
     * Get the active price for products.
     */
    private readonly _getActivePrice = async (): Promise<void> => {
        const productsList = this.state.products;
        const products = await getPriceForProductSearchResult(
            new PriceForProductSearchResultInput(productsList),
            this.props.context.actionContext
        );

        await this._getProductsDimensionsAvailabilities(products);

        this.setState({ products });
    };
}

export default ProductCollection;
